import { useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import Button, { ButtonGroup } from "@atlaskit/button";
import StatusIcon from "@atlaskit/icon/core/status-information";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import RandomizeIcon from "@atlaskit/icon-lab/core/randomize";

import { wacToken } from "@/libraries/design/foundations";
import { messages } from "@/templates/paid-landing/sections/cfind-4666-frictionless-first-look/messages.ts";
import { useJiraAiFFL } from "@/templates/paid-landing/src/hooks/use-jira-ai-ffl-context";

import { TeamType, teamTypesMap } from "../../utils/paid-jira-ai-ffl.ts";

import { PaidJiraAiMadLib } from "./paid-jira-ai-mad-lib.tsx";

const containerStyles = css({
    marginBlockStart: "48px",
    textAlign: "center"
});

const modalStyles = css({
    display: "inline-block",
    width: "845px",
    boxShadow:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "0px 0px 1px rgba(30, 31, 33, 0.31), 0px 8px 12px rgba(30, 31, 33, 0.15)",
    borderRadius: "16px",
    background: wacToken("color.neutral.light0"),
    padding: "24px 20px",
    textAlign: "left"
});

const teamTypeContainerStyles = css({
    marginBlockEnd: "16px"
});

const teamTypeButtonStyles = {
    fontWeight: 400,
    borderRadius: 8
};

const footerStyles = css({
    marginBlockStart: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline"
});

const disclaimerStyles = css({
    fontFamily: "var(--atlassian-sans-font-family)",
    fontSize: "12px",
    lineHeight: "16px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#6B6E76"
});

const poweredByAiStyles = css({
    display: "flex",
    alignItems: "center",
    gap: "4px"
});

const footerButtonStyles = {
    borderRadius: "40px",
    paddingLeft: "24px",
    paddingRight: "24px"
};

export interface MadlibSubmitParams {
    teamType: TeamType;
    workingOnValue: string;
    goalValue: string;
}

export interface PaidJiraAiTeamTypeMadLibPromptProp {
    isRerun?: boolean;
    showLoadingState?: boolean;
}

export const PaidJiraAiTeamTypeMadLibPrompt = ({
    isRerun,
    showLoadingState
}: PaidJiraAiTeamTypeMadLibPromptProp) => {
    const {
        submit,
        cgsDataLoading,
        selectedTeamType,
        setSelectedTeamType,
        workingOnSuggestion,
        goalSuggestion,
        workingOnValue,
        goalValue,
        setWorkingOnValue,
        setGoalValue,
        setHasUserSelectedTeamType,
        hasUserSelectedTeamType,
        shuffle,
        suggestMadLib,
        setIsErrorFromCgs,
        firePaidJiraAi,
        markPromptChangedField,
        commonAnalyticsAttributes,
        promptAttemptIndex
    } = useJiraAiFFL();

    const [isFormValid, setIsFormValid] = useState(true);

    const handleOnSubmit = async () => {
        if (!isFormValid) {
            return;
        }

        // Track click intent separately from the async lifecycle events in the context.
        firePaidJiraAi("BuildWithAIClicked", "clicked", {
            cta: isRerun ? "rerunPrompt" : "getStarted",
            promptAttemptIndex: promptAttemptIndex + 1
        });

        const params: MadlibSubmitParams = {
            teamType: selectedTeamType,
            workingOnValue: workingOnValue || workingOnSuggestion,
            goalValue: goalValue || goalSuggestion
        };

        await submit({
            teamType: params.teamType,
            workingOn: params.workingOnValue,
            goal: params.goalValue
        });
    };

    return (
        <div css={containerStyles}>
            <div css={[modalStyles]}>
                {!isRerun ? (
                    <div css={teamTypeContainerStyles}>
                        <ButtonGroup>
                            {Object.entries(teamTypesMap).map(
                                ([teamType, { messageKey }]) => (
                                    <Button
                                        key={teamType}
                                        appearance={
                                            selectedTeamType === teamType
                                                ? "subtle"
                                                : "default"
                                        }
                                        isSelected={
                                            selectedTeamType === teamType
                                        }
                                        style={teamTypeButtonStyles}
                                        onClick={() => {
                                            markPromptChangedField("teamType");
                                            firePaidJiraAi(
                                                "PromptTeamTypeSelected",
                                                "clicked",
                                                {
                                                    previousTeamType:
                                                        selectedTeamType,
                                                    newTeamType: teamType,
                                                    isFirstSelection:
                                                        !hasUserSelectedTeamType
                                                }
                                            );
                                            setHasUserSelectedTeamType(true);
                                            setSelectedTeamType(
                                                teamType as TeamType
                                            );
                                            suggestMadLib(teamType as TeamType);
                                        }}
                                    >
                                        <FormattedMessage
                                            {...messages[messageKey]}
                                        />
                                    </Button>
                                )
                            )}
                        </ButtonGroup>
                    </div>
                ) : null}
                <PaidJiraAiMadLib
                    focusInput={!isRerun}
                    workingOnSuggestion={workingOnSuggestion}
                    goalSuggestion={goalSuggestion}
                    workingOnValue={workingOnValue}
                    goalValue={goalValue}
                    onEnter={handleOnSubmit}
                    onChange={({
                        workingOnValue: newWorkingOnValue,
                        goalValue: newGoalValue
                    }) => {
                        if (newWorkingOnValue !== workingOnValue) {
                            setIsErrorFromCgs(false);
                            setWorkingOnValue(newWorkingOnValue);
                            if (
                                !commonAnalyticsAttributes.changedFields?.includes(
                                    "workingOn"
                                )
                            ) {
                                firePaidJiraAi(
                                    "PromptFieldChanged",
                                    "clicked",
                                    {
                                        field: "workingOn",
                                        valueLength:
                                            newWorkingOnValue.trim().length,
                                        isCustom: Boolean(
                                            newWorkingOnValue.trim()
                                        )
                                    }
                                );
                            }
                            markPromptChangedField("workingOn");
                        }
                        if (newGoalValue !== goalValue) {
                            setIsErrorFromCgs(false);
                            setGoalValue(newGoalValue);
                            if (
                                !commonAnalyticsAttributes.changedFields?.includes(
                                    "goal"
                                )
                            ) {
                                firePaidJiraAi(
                                    "PromptFieldChanged",
                                    "clicked",
                                    {
                                        field: "goal",
                                        valueLength: newGoalValue.trim().length,
                                        isCustom: Boolean(newGoalValue.trim())
                                    }
                                );
                            }
                            markPromptChangedField("goal");
                        }
                    }}
                    setAreInputsValid={setIsFormValid}
                />
                <div css={footerStyles}>
                    <div css={disclaimerStyles}>
                        {!isRerun ? (
                            <div css={poweredByAiStyles}>
                                <FormattedMessage {...messages.poweredByAi} />
                                <StatusIcon label="Status info" />
                            </div>
                        ) : null}
                    </div>
                    <ButtonGroup>
                        {cgsDataLoading || showLoadingState ? (
                            <Button
                                isDisabled
                                appearance="subtle"
                                style={footerButtonStyles}
                            >
                                <FormattedMessage {...messages.working} />
                            </Button>
                        ) : (
                            <>
                                <Button
                                    appearance="subtle"
                                    iconBefore={<RandomizeIcon label="" />}
                                    style={footerButtonStyles}
                                    onClick={shuffle}
                                >
                                    <FormattedMessage {...messages.shuffle} />
                                </Button>
                                {isRerun ? (
                                    <Button
                                        isDisabled={!isFormValid}
                                        appearance="subtle"
                                        iconBefore={
                                            <VideoPlayOverlayIcon label="" />
                                        }
                                        style={footerButtonStyles}
                                        onClick={handleOnSubmit}
                                    >
                                        <FormattedMessage
                                            {...messages.rerunPrompt}
                                        />
                                    </Button>
                                ) : (
                                    <Button
                                        isDisabled={!isFormValid}
                                        appearance="primary"
                                        style={footerButtonStyles}
                                        onClick={handleOnSubmit}
                                    >
                                        <FormattedMessage
                                            {...messages.getStarted}
                                        />
                                    </Button>
                                )}
                            </>
                        )}
                    </ButtonGroup>
                </div>
            </div>
        </div>
    );
};
